import MUIIconDoneRounded from '@mui/icons-material/DoneRounded';
import { StepIconProps as MUIStepIconProps, styled } from '@mui/material';
import MUIStepIcon, { type StepIconProps } from '@mui/material/StepIcon';
import type { ElementType } from 'react';

const shouldForwardProp = (prop: string) =>
  prop !== 'largeText' && prop !== 'lightIcons' && prop !== 'blackAndWhiteActiveIcon';

const DoneIcon = styled(MUIIconDoneRounded, { shouldForwardProp })<{ lightIcons: boolean }>`
  ${({ lightIcons, theme }) =>
    lightIcons
      ? /* istanbul ignore next */ `background-color: ${theme.palette.primary.light};`
      : `background-color: ${theme.palette.grey[900]};`}
  border-radius: 50%;
  color: ${({ theme }) => theme.palette.primary.contrastText};
`;

const ActiveIcon = styled(MUIStepIcon, { shouldForwardProp })<{
  lightIcons: boolean;
  blackAndWhiteActiveIcon?: boolean;
}>`
  ${({ lightIcons, blackAndWhiteActiveIcon, theme }) => {
    /* istanbul ignore if */
    if (blackAndWhiteActiveIcon) {
      return `color: black;`;
    }

    return lightIcons
      ? /* istanbul ignore next */ `color: ${theme.palette.primary.light};`
      : `color: ${theme.palette.primary.main};`;
  }}
  & .MuiStepIcon-text {
    fill: ${({ theme, blackAndWhiteActiveIcon }) =>
      blackAndWhiteActiveIcon ? /* istanbul ignore next */ 'white' : theme.palette.primary.contrastText};
  }
`;

const InactiveIcon = styled(MUIStepIcon, { shouldForwardProp })`
  color: ${({ theme }) => theme.palette.grey[500]};
  & .MuiStepIcon-text {
    fill: ${({ theme }) => theme.palette.primary.contrastText};
  }
`;

export function CustomIcon({
  completed,
  active,
  icon,
  largeText,
  lightIcons,
  blackAndWhiteActiveIcon,
}: MUIStepIconProps & {
  largeText: boolean;
  lightIcons: boolean;
  blackAndWhiteActiveIcon?: boolean;
}) {
  const NextIcon = active ? ActiveIcon : InactiveIcon; // inactive gives a darker background to the icon
  return completed ? (
    <DoneIcon sx={largeText ? /* istanbul ignore next */ { fontSize: 32 } : undefined} lightIcons={lightIcons} />
  ) : (
    <NextIcon
      icon={icon}
      sx={largeText ? /* istanbul ignore next */ { fontSize: 32 } : undefined}
      lightIcons={lightIcons}
      blackAndWhiteActiveIcon={blackAndWhiteActiveIcon}
    />
  ); // icon prop contains the number of the step by default
}

export const createStepIconComponent = (
  largeText: boolean,
  lighterIcons: boolean,
  blackAndWhiteActiveIcon?: boolean
): ElementType<StepIconProps> => {
  function StepIconComponent(props: Readonly<MUIStepIconProps>) {
    return (
      <CustomIcon
        {...props}
        largeText={largeText}
        lightIcons={lighterIcons}
        blackAndWhiteActiveIcon={blackAndWhiteActiveIcon}
      />
    );
  }
  return StepIconComponent;
};
